export type DiffLine = {
  type: "added" | "removed" | "unchanged";
  line: string;
  lineNumber?: number;
  originalLineNumber?: number;
};

// Simple line-by-line diff using LCS
export function generateLineDiff(originalText: string, modifiedText: string): DiffLine[] {
  const originalLines = originalText === "" ? [] : originalText.split("\n");
  const modifiedLines = modifiedText === "" ? [] : modifiedText.split("\n");

  const m = originalLines.length;
  const n = modifiedLines.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (originalLines[i - 1] === modifiedLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const result: DiffLine[] = [];
  let i = m,
    j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && originalLines[i - 1] === modifiedLines[j - 1]) {
      result.unshift({ type: "unchanged", line: originalLines[i - 1], originalLineNumber: i, lineNumber: j });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", line: modifiedLines[j - 1], lineNumber: j });
      j--;
    } else if (i > 0) {
      result.unshift({ type: "removed", line: originalLines[i - 1], originalLineNumber: i });
      i--;
    }
  }
  return result;
}

export type GroupedDiffItem =
  | {
      type: "group";
      items: DiffLine[];
      startLine: number;
      endLine: number;
      groupId: number;
    }
  | DiffLine;

export function groupDiff(diffResult: DiffLine[], contextGroupMin: number = 5): GroupedDiffItem[] {
  const grouped: GroupedDiffItem[] = [];
  let currentGroup: DiffLine[] = [];
  let groupId = 0;

  const pushGroup = () => {
    if (currentGroup.length === 0) return;
    if (currentGroup.length >= contextGroupMin) {
      grouped.push({
        type: "group",
        items: currentGroup,
        startLine: currentGroup[0].originalLineNumber || 0,
        endLine: currentGroup[currentGroup.length - 1].originalLineNumber || 0,
        groupId: groupId++,
      });
    } else {
      currentGroup.forEach((l) => grouped.push(l));
    }
    currentGroup = [];
  };

  diffResult.forEach((item) => {
    if (item.type === "unchanged") {
      currentGroup.push(item);
    } else {
      pushGroup();
      grouped.push(item);
    }
  });
  pushGroup();
  return grouped;
}

export function countChanges(diffResult: DiffLine[]): { added: number; removed: number } {
  return {
    added: diffResult.filter((d) => d.type === "added").length,
    removed: diffResult.filter((d) => d.type === "removed").length,
  };
}


